import {
  Form,
  required,
  SelectInput,
  useCreate,
  useNotify,
  useRefresh,
} from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useGateValue } from '@statsig/react-bindings';
import PropTypes from 'prop-types';

import { useDeclineReasonOptions } from '@pumpkincare/wellness';

import RoutePaths from '../../../routes';
import { parseAndFormatDate, STATUSES } from '../../../shared/utils';
import { cancelWellnessModalStyle } from './cancel-wellness-modal-style';

function CancelWellnessModal(props) {
  const { onClose, isOpen, wellness = {} } = props;
  const classes = cancelWellnessModalStyle();
  const [create, { isLoading }] = useCreate();
  const refresh = useRefresh();
  const notify = useNotify();

  const { data: declineReasonOptions } = useDeclineReasonOptions();
  const canCancelPendingWellness = useGateValue('fer-7526-cancel-pending-wellness');
  const isCancellingPendingWellness =
    canCancelPendingWellness && wellness.status === STATUSES.PENDING;

  function renderCancellationText() {
    if (isCancellingPendingWellness) {
      return `This wellness membership will be deactivated. This pet will no longer
        have wellness coverage as of this membership's effective date, and any lapsed
        wellness invoices will be labeled as uncollectible. This will not affect
        associated policies, pet plans, or their invoices.`;
    }

    return `This plan will not be activated. This pet will no longer have
        coverage as of ${parseAndFormatDate(wellness.end_date)}`;
  }

  function handleSubmitCancellationReason({ cancellation_reason }) {
    const { resource, data } = isCancellingPendingWellness
      ? {
          resource: `${RoutePaths.wellness}/cancel-pending-wellness`,
          data: { wellness_id: wellness.id, pet_id: wellness.pet_id },
        }
      : {
          resource: `cancellations/renewals-v2`,
          data: {
            reasons: [{ decline_enum: cancellation_reason }],
            pet_id: wellness.pet_id,
            decline_list: ['wellness'],
          },
        };
    create(
      resource,
      { data },
      {
        onSuccess: () => {
          if (isCancellingPendingWellness) {
            notify(
              'The pending wellness membership has been successfully cancelled.',
              {
                type: 'success',
              }
            );
          }
          onClose();
          refresh();
        },
        onError: error => {
          const message =
            error?.status && error.status < 500
              ? error.message
              : 'Something went wrong. Please try again later.';
          notify(
            `There was an error while cancelling the wellness ${
              isCancellingPendingWellness ? 'membership' : 'renewal plan'
            }. ${message}`,
            {
              type: 'warning',
            }
          );
        },
      }
    );
  }

  return (
    <Dialog maxWidth='md' open={isOpen} onClose={onClose}>
      <Form onSubmit={handleSubmitCancellationReason}>
        <DialogTitle>
          <Typography className={classes.modalTitle}>
            {isCancellingPendingWellness
              ? 'Confirm Cancellation'
              : 'Confirm Non-Renewal'}
          </Typography>
          <Typography className={classes.modalSubTitle}>
            {renderCancellationText()}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div className={classes.informationDiv}>
            <Typography className={classes.information}>PET:</Typography>
            <Typography className={classes.information}>
              {wellness.pet_name}
            </Typography>
          </div>

          <div className={classes.informationDiv}>
            <Typography className={classes.information}>PLAN PERIOD:</Typography>
            <Typography className={classes.information}>
              {`${parseAndFormatDate(wellness.start_date)} - ${parseAndFormatDate(
                wellness.end_date
              )}`}
            </Typography>
          </div>
          {!isCancellingPendingWellness ? (
            <SelectInput
              fullWidth
              validate={required()}
              source='cancellation_reason'
              choices={declineReasonOptions}
              label='Cancellation Reason'
            />
          ) : null}
        </DialogContent>
        <DialogActions style={{ justifyContent: 'space-between' }}>
          <Button
            disabled={isLoading}
            onClick={onClose}
            className={classes.cancelButton}
          >
            CANCEL
          </Button>
          <Button
            disabled={isLoading}
            type='submit'
            className={classes.updateButton}
          >
            CONFIRM
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}

CancelWellnessModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  wellness: PropTypes.object,
};

export default CancelWellnessModal;
